import { useEffect } from 'react';

export interface KeyboardShortcut {
	/** Lowercase key, e.g. `"k"`, `"l"`, `"escape"`. */
	key: string;
	/**
	 * Required modifier keys. `meta` maps to Cmd on macOS and Ctrl on
	 * other platforms — use it for "primary" shortcuts. Omit for
	 * modifier-less keys (e.g. `"escape"`).
	 */
	modifiers?: ReadonlyArray<'shift' | 'alt' | 'meta'>;
	/** Human-readable description, useful for command palettes / a11y listings. */
	description?: string;
	/** Handler — receives the original event so it can call `preventDefault`. */
	handler: (event: KeyboardEvent) => void;
	/**
	 * Whether to skip the handler when focus is inside an editable field
	 * (`<input>`, `<textarea>`, `[contenteditable]`). Defaults to `true`.
	 */
	ignoreEditable?: boolean;
}

const IS_MAC: boolean = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform);

/**
 * Global keyboard shortcut handler.
 *
 * Each shortcut's `key` is matched case-insensitively. The platform's
 * primary modifier (Cmd on macOS, Ctrl elsewhere) is what `meta` in
 * `modifiers` resolves to — so `[ 'meta' ]` matches Cmd+K on Mac and
 * Ctrl+K on Windows. Combine with `'shift'`, `'alt'` for combos.
 * Omit all modifiers for keys like `Escape`.
 *
 * The listener is attached to `window` and only fires when no editable
 * element has focus (unless `ignoreEditable: false`). This keeps text
 * entry snappy and avoids hijacking the user's keystrokes inside forms.
 */
export function useKeyboardShortcuts(shortcuts: ReadonlyArray<KeyboardShortcut>): void {
	useEffect(() => {
		if (typeof window === 'undefined') return;

		const onKeyDown = (event: KeyboardEvent): void => {
			const key = event.key.toLowerCase();

			for (const shortcut of shortcuts) {
				if (shortcut.key.toLowerCase() !== key) continue;

				const ignoreEditable = shortcut.ignoreEditable ?? true;
				if (ignoreEditable && isEditableTarget(event.target)) continue;

				const mods = shortcut.modifiers ?? [];
				const needsShift = mods.includes('shift');
				const needsAlt = mods.includes('alt');
				const needsMeta = mods.includes('meta');

				if (event.shiftKey !== needsShift) continue;
				if (event.altKey !== needsAlt) continue;

				if (needsMeta) {
					// Cmd on Mac, Ctrl on other platforms.
					const primaryHeld = IS_MAC ? event.metaKey : event.ctrlKey;
					if (!primaryHeld) continue;
				} else {
					// If the user did press the primary modifier, treat this as a
					// different combo and let it fall through to the browser.
					const primaryHeld = IS_MAC ? event.metaKey : event.ctrlKey;
					if (primaryHeld) continue;
				}

				event.preventDefault();
				shortcut.handler(event);
				return;
			}
		};

		window.addEventListener('keydown', onKeyDown);
		return () => {
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [shortcuts]);
}

function isEditableTarget(target: EventTarget | null): boolean {
	if (!(target instanceof HTMLElement)) return false;
	if (target.isContentEditable) return true;
	const tag = target.tagName;
	return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
